import type { FunctionComponent } from "react";
import * as React from "react";
import { useMemo } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader, PlainTooltip } from "@similarweb/ui-components";

import QualificationSideBarContext from "app/components/QualificationSideBar/QualificationSidebarContext";
import { i18nFilter } from "app/filters/i18nFilter";
import { categoryIconFilter } from "app/filters/ngFilters";
import { replaceUnderscores } from "app/pages/workspace/sales/sub-modules/feed/helpers";
import { useSiteInfo } from "app/services/sitesService/hooks/useSiteInfo";
import { getSiteDataFromResponse } from "app/services/sitesService/sitesUtils";
import type { ISiteInfoItem } from "app/services/sitesService/types";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const CategoryContainer = styled.div`
    max-width: 38ch;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
`;

const IconContainer = styled.div`
    color: ${colorsPalettes.carbon["200"]};
    .sprite-category {
        font-size: 19px;
        padding-right: 4px;
    }
`;

const Badge = styled(FlexRow)`
    background-color: ${colorsPalettes.carbon["50"]};
    color: ${colorsPalettes.carbon["400"]};
    font-weight: 500;
    font-size: 14px;
    margin-right: 10px;
    align-items: center;
    height: 28px;
    padding: 0 8px;
    border-radius: 4px;
`;

const SiteInfoContainer = styled(FlexRow)`
    min-height: 76px;
    align-items: center;
`;

const SiteInfoEmptyContainer = styled.div`
    height: 24px;
`;

interface ISiteInfoProps {
    country: string;
}

const SiteInfo: FunctionComponent<ISiteInfoProps> = ({ country }) => {
    const { qualificationSideBarSelection } = React.useContext(QualificationSideBarContext);
    const domain = qualificationSideBarSelection?.data?.domain;
    const i18n = i18nFilter();

    const apiParams = useMemo(() => {
        return {
            keys: domain,
            mainDomainOnly: true,
            includeCrossData: true,
            country,
        };
    }, [domain, country]);

    const {
        data: siteInfoResponse,
        isLoading: isSiteIndustryLoading,
        isError,
    } = useSiteInfo(apiParams, undefined, {
        enabled: !!domain,
    });

    const siteIndustryData: ISiteInfoItem | undefined = useMemo(() => {
        if (!siteInfoResponse || !domain) {
            return undefined;
        }
        return getSiteDataFromResponse(siteInfoResponse, domain);
    }, [siteInfoResponse, domain]);

    const checkIfNoData = () => {
        return !siteIndustryData || isError;
    };

    const formatCategory = (str) => {
        const strArray = str.split("/");
        return strArray[1] ? strArray[1] : strArray[0];
    };

    const getContent = () => (
        <FlexRow>
            <PlainTooltip
                maxWidth={220}
                tooltipContent={i18n("qualification.sidebar.category.tooltip")}
            >
                <Badge>
                    <IconContainer>
                        <i className={`sprite-category ${categoryIconFilter()(siteIndustryData.category)}`} />
                    </IconContainer>
                    <CategoryContainer>
                        {siteIndustryData.category
                            ? formatCategory(siteIndustryData.category)
                            : i18n("qualification.sidebar.site.info.no-category")}
                    </CategoryContainer>
                </Badge>
            </PlainTooltip>
            {siteIndustryData.functionality ? (
                <PlainTooltip
                    maxWidth={220}
                    tooltipContent={i18n("qualification.sidebar.industry.tooltip")}
                >
                    <Badge>{replaceUnderscores(siteIndustryData.functionality as string)}</Badge>
                </PlainTooltip>
            ) : null}
        </FlexRow>
    );

    return isSiteIndustryLoading ? (
        <SiteInfoContainer>
            <PixelPlaceholderLoader
                width={"330px"}
                height={"20px"}
            />
        </SiteInfoContainer>
    ) : checkIfNoData() ? (
        <SiteInfoEmptyContainer />
    ) : (
        <SiteInfoContainer>{getContent()}</SiteInfoContainer>
    );
};
export const MemoizedSiteInfo = React.memo(SiteInfo);
